import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import {
  ClearOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { isPlatformOperatorUser } from '../../assets/utils/permissions';
import DashboardSection from '../dashboard/components/DashboardSection';

const { Text, Paragraph, Title } = Typography;

const PRODUCTS = [
  {
    value: 'care-provider',
    label: 'care-provider',
    hint: 'Facility act-as — used when logging into care-provider (Application 3)',
  },
  {
    value: 'care',
    label: 'care (member)',
    hint: 'Policy / client act-as — used when logging into care (Application 4)',
  },
  {
    value: 'aman-hr',
    label: 'aman-hr',
    hint: 'Organization act-as — used when logging into aman-hr (Application 5)',
  },
];

function contextSummary(ctx) {
  if (!ctx) return '—';
  const bits = [];
  if (ctx.facility_id) bits.push(`facility #${ctx.facility_id}`);
  if (ctx.policy_no) bits.push(`policy ${ctx.policy_no}`);
  if (ctx.client_id) bits.push(`client #${ctx.client_id}`);
  if (ctx.org_id) bits.push(`org #${ctx.org_id}`);
  if (ctx.org_code) bits.push(`code ${ctx.org_code}`);
  if (ctx.org_social_no) bits.push(`social ${ctx.org_social_no}`);
  return bits.length ? bits.join(' · ') : '—';
}

const PlatformContextsPage = () => {
  const { user } = useAuth();
  const allowed = useMemo(() => isPlatformOperatorUser(user), [user]);
  const selfId = Number(user?.id || user?.profile?.id || 0);

  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.listPlatformOperators();
      if (resp?.error) return;
      const rows = resp?.data?.data?.operators || resp?.data?.operators || [];
      setOperators(Array.isArray(rows) ? rows : []);
      setSelectedUserId((prev) => {
        if (prev) return prev;
        const me = rows.find((r) => Number(r.user_id) === selfId);
        return me?.user_id ?? rows[0]?.user_id ?? selfId ?? null;
      });
    } finally {
      setLoading(false);
    }
  }, [selfId]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const selected = useMemo(
    () => operators.find((o) => Number(o.user_id) === Number(selectedUserId)) || null,
    [operators, selectedUserId]
  );

  const contextsByProduct = useMemo(() => {
    const map = {};
    (selected?.contexts || []).forEach((c) => {
      map[c.product] = c;
    });
    return map;
  }, [selected]);

  const openEdit = (product) => {
    setEditingProduct(product);
    const existing = contextsByProduct[product] || {};
    form.setFieldsValue({
      facility_id: existing.facility_id ?? undefined,
      client_id: existing.client_id ?? undefined,
      policy_no: existing.policy_no ?? undefined,
      org_id: existing.org_id ?? undefined,
      org_code: existing.org_code ?? undefined,
      org_social_no: existing.org_social_no ?? undefined,
      reason: undefined,
    });
    setModalOpen(true);
  };

  const saveContext = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        user_id: Number(selectedUserId),
        product: editingProduct,
        reason: values.reason || undefined,
        facility_id: values.facility_id || undefined,
        client_id: values.client_id || undefined,
        policy_no: values.policy_no || undefined,
        org_id: values.org_id || undefined,
        org_code: values.org_code || undefined,
        org_social_no: values.org_social_no || undefined,
      };
      const resp = await lib.setPlatformOperatorContext(payload);
      if (resp?.error) return;
      notification.success({ message: resp?.data?.message || 'Context saved' });
      setModalOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const clearProduct = async (product) => {
    const resp = await lib.clearPlatformOperatorContext({
      user_id: Number(selectedUserId),
      product,
    });
    if (resp?.error) return;
    notification.success({ message: resp?.data?.message || 'Context cleared' });
    await load();
  };

  const clearAll = async () => {
    const resp = await lib.clearPlatformOperatorContext({
      user_id: Number(selectedUserId),
      product: 'all',
    });
    if (resp?.error) return;
    notification.success({ message: resp?.data?.message || 'All contexts cleared' });
    await load();
  };

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Platform operators only"
        subTitle="Pre-assigned act-as contexts are managed by platform operators (platform.*)."
      />
    );
  }

  const productMeta = PRODUCTS.find((p) => p.value === editingProduct);

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}>Platform act-as contexts</Title>
      <Paragraph type="secondary">
        Pre-assign which facility, policy, or organization you act as. Then log into
        care-provider / care / aman-hr with normal staff credentials — no FE changes in those apps.
      </Paragraph>

      <DashboardSection
        id="platform-contexts"
        title="Assignments"
        subtitle={selected?.staffId ? `Editing ${selected.staffId}` : 'Select an operator'}
        collapsible={false}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Session binding only"
          description="These assignments shape the JWT on login. They do not create facility staff or HR membership rows. care-admin login is unchanged."
        />

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ minWidth: 280 }}
            value={selectedUserId}
            onChange={setSelectedUserId}
            options={operators.map((o) => ({
              value: o.user_id,
              label: `${o.staffId || 'staff'} (#${o.user_id})${Number(o.user_id) === selfId ? ' — you' : ''}`,
            }))}
            placeholder="Select platform operator"
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Popconfirm
            title="Clear all product contexts for this operator?"
            onConfirm={clearAll}
            okText="Clear all"
          >
            <Button danger icon={<ClearOutlined />} disabled={!selected}>
              Clear all
            </Button>
          </Popconfirm>
        </Space>

        <Table
          loading={loading}
          pagination={false}
          rowKey="value"
          dataSource={PRODUCTS}
          columns={[
            {
              title: 'Product',
              dataIndex: 'label',
              render: (label, row) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{label}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{row.hint}</Text>
                </Space>
              ),
            },
            {
              title: 'Assigned context',
              key: 'ctx',
              render: (_, row) => {
                const ctx = contextsByProduct[row.value];
                return ctx ? (
                  <Space direction="vertical" size={0}>
                    <Tag color="blue">{contextSummary(ctx)}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      updated {ctx.updated_at || '—'}
                      {ctx.updated_by ? ` · by #${ctx.updated_by}` : ''}
                    </Text>
                  </Space>
                ) : (
                  <Tag>not set</Tag>
                );
              },
            },
            {
              title: '',
              key: 'actions',
              width: 200,
              render: (_, row) => (
                <Space>
                  <Button size="small" type="primary" onClick={() => openEdit(row.value)}>
                    Set
                  </Button>
                  <Popconfirm
                    title={`Clear ${row.label} context?`}
                    onConfirm={() => clearProduct(row.value)}
                    disabled={!contextsByProduct[row.value]}
                  >
                    <Button size="small" disabled={!contextsByProduct[row.value]}>
                      Clear
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </DashboardSection>

      <Card size="small" style={{ marginTop: 16 }} title="How login works">
        <Paragraph style={{ marginBottom: 8 }}>
          After saving a context, sign into that product with your staff email and password
          (Application header 3 / 4 / 5). The API merges the pre-assigned context into the session.
          Missing or inactive targets fail closed with a clear error.
        </Paragraph>
        <Text type="secondary">
          CLI: scripts/platform_operator_context_set.php · clear.php · list.php
        </Text>
      </Card>

      <Modal
        title={`Set ${productMeta?.label || 'product'} context`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={saveContext}
        confirmLoading={saving}
        okText="Save"
        okButtonProps={{ icon: <SaveOutlined /> }}
        destroyOnClose
      >
        <Paragraph type="secondary">{productMeta?.hint}</Paragraph>
        <Form form={form} layout="vertical">
          {editingProduct === 'care-provider' && (
            <Form.Item
              name="facility_id"
              label="Facility ID"
              rules={[{ required: true, message: 'facility_id is required' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} placeholder="e.g. 42" />
            </Form.Item>
          )}
          {editingProduct === 'care' && (
            <>
              <Form.Item name="policy_no" label="Policy number">
                <Input placeholder="AH/..." />
              </Form.Item>
              <Form.Item name="client_id" label="Client ID">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Text type="secondary">Provide policy_no and/or client_id.</Text>
            </>
          )}
          {editingProduct === 'aman-hr' && (
            <>
              <Form.Item name="org_id" label="Organization ID">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Form.Item name="org_code" label="Org code">
                <Input />
              </Form.Item>
              <Form.Item name="org_social_no" label="Org social number">
                <Input />
              </Form.Item>
              <Text type="secondary">Provide org_id, org_code, and/or org_social_no.</Text>
            </>
          )}
          <Form.Item name="reason" label="Reason (audit)" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} placeholder="Why this context?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlatformContextsPage;
